import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isInSameRangeClock } from '../../lib/dateUtils';

/**
 * An analog clock with clock face and clock hand.
 * @property {number} hour Selected hour
 * @property {number} minute Selected minute
 * @property {number} second Selected second
 * @property {string} displayMode Current display mode, shows either hour, minute or seconds. Values: 'hh', 'mm' or 'ss'
 * @property {number} size The diameter of the clock
 * @property {number} hour24 If true show 24-hour clock, if false show 12-hour clock
 *
 * @extends React.component
 * @see Timepicker
 */
export default class Clock extends Component {
  static displayName = 'Clock';

  static propTypes = {
    size: PropTypes.number,
    hour24: PropTypes.bool,
    hour: PropTypes.number,
    minute: PropTypes.number,
    second: PropTypes.number,
    displayMode: PropTypes.string,
    onChange: PropTypes.func,
  };

  static defaultProps = {
    size: 300,
    hour24: false,
    hour: 0,
    minute: 0,
    second: 0,
    displayMode: 'hh',
  };

  constructor(props, context) {
    super(props, context);
    this.getCoordinatesHour = this.getCoordinatesHour.bind(this);
    this.getCoordinates = this.getCoordinates.bind(this);
    this.renderHours = this.renderHours.bind(this);
    this.renderOther = this.renderMinutes.bind(this);
    this.renderDisplayMode = this.renderDisplayMode.bind(this);
    this.renderClockHand = this.renderClockHand.bind(this);
    this.onClickHour = this.onClickHour.bind(this);
    this.onClickMinute = this.onClickMinute.bind(this);
    this.onClickSecond = this.onClickSecond.bind(this);
    this.state = {};
  }

  onClickHour(i) {
    this.props.onChange(i, this.props.minute, this.props.second);
  }

  onClickMinute(i) {
    this.props.onChange(this.props.hour, i, this.props.second);
  }

  onClickSecond(i) {
    this.props.onChange(this.props.hour, this.props.minute, i);
  }

  getCoordinatesHour() {
    const coordinates = [];
    const radius = (this.props.size / 2) * 0.8;

    for (let i = 1; i <= (this.props.hour24 ? 24 : 12); i++) {
      let positionFromCenter = radius;
      if (this.props.hour24 && i > 12) {
        positionFromCenter *= 0.65;
      }
      coordinates.push([
        Math.round(this.props.size / 2 + positionFromCenter * Math.cos(((i % 12) / 6 - 0.5) * Math.PI)),
        Math.round(this.props.size / 2 + positionFromCenter * Math.sin(((i % 12) / 6 - 0.5) * Math.PI)),
      ]);
    }

    return coordinates;
  }

  getCoordinates() {
    const coordinates = [];
    const radius = (this.props.size / 2) * 0.8;

    for (let i = 0; i < 60; i++) {
      coordinates.push([
        Math.round(this.props.size / 2 + radius * Math.cos((i / 30 - 0.5) * Math.PI)),
        Math.round(this.props.size / 2 + radius * Math.sin((i / 30 - 0.5) * Math.PI)),
      ]);
    }

    return coordinates;
  }

  renderClockHand() {
    const coordinates = this.props.displayMode === 'hh' ? this.getCoordinatesHour() : this.getCoordinates();
    if (this.props.displayMode === 'mm') {
      return (
        <line
          key='clockhand-minute'
          className='clockhand'
          data-testid='clockhand'
          x1={this.props.size / 2}
          x2={coordinates[this.props.minute][0]}
          y1={this.props.size / 2}
          y2={coordinates[this.props.minute][1]}
        />
      );
    } else if (this.props.displayMode === 'ss') {
      return (
        <line
          key='clockhand-second'
          className='clockhand'
          data-testid='clockhand'
          x1={this.props.size / 2}
          x2={coordinates[this.props.second][0]}
          y1={this.props.size / 2}
          y2={coordinates[this.props.second][1]}
        />
      );
    }

    const selectedHour = this.props.hour % (this.props.hour24 ? 24 : 12);
    let index = selectedHour === 0 ? 11 : selectedHour - 1;
    if (this.props.hour24) {
      index = selectedHour === 0 ? 23 : selectedHour - 1;
    }
    return (
      <line
        key='clockhand-hour'
        className='clockhand'
        data-testid='clockhand'
        x1={this.props.size / 2}
        x2={coordinates[index][0]}
        y1={this.props.size / 2}
        y2={coordinates[index][1]}
      />
    );
  }

  renderHours() {
    const selectedHour = this.props.hour % (this.props.hour24 ? 24 : 12);
    const coordinates = this.getCoordinatesHour();
    const elements = [];

    for (let i = 0; i < coordinates.length; i++) {
      const cx = coordinates[i][0];
      const cy = coordinates[i][1];
      const label = (i + 1) % 24;
      const isItemSelected = selectedHour === label || (!this.props.hour24 && selectedHour === 0 && label === 12);
      const cn = classnames('item', {
        'item--selected': isItemSelected,
        'item--unfocused': this.props.hour24 && !isInSameRangeClock(selectedHour, label),
        'item--focused': this.props.hour24 && isInSameRangeClock(selectedHour, label),
      });
      const element = (
        <g key={label} className={cn} data-testid={isItemSelected ? 'selectedHour' : undefined} onClick={this.onClickHour.bind(this, label)}>
          <circle cx={cx} cy={cy} />
          <text x={cx} y={cy}>
            {label}
          </text>
        </g>
      );
      elements.push(element);
    }

    return <g key='clock-hours'>{elements}</g>;
  }

  renderMinutes(selected, callback, key) {
    const coordinates = this.getCoordinates();
    const elements = [];

    for (let i = 0; i < coordinates.length; i++) {
      const cx = coordinates[i][0];
      const cy = coordinates[i][1];

      const cn = classnames('item', {
        'item--selected': selected === i,
        'item--small': i % 5 !== 0,
      });
      const testId = key === 'clock-minutes' ? 'selectedMinute' : 'selectedSecond';

      const element = (
        <g key={i} className={cn} data-testid={selected === i ? testId : undefined} onClick={callback.bind(this, i)}>
          <circle cx={cx} cy={cy} />
          {i % 5 === 0 ? (
            <text x={cx} y={cy}>
              {i}
            </text>
          ) : null}
        </g>
      );
      elements.push(element);
    }

    return <g key={key}>{elements}</g>;
  }

  renderDisplayMode() {
    if (this.props.displayMode === 'mm') {
      return this.renderOther(this.props.minute, this.onClickMinute, 'clock-minutes');
    } else if (this.props.displayMode === 'ss') {
      return this.renderOther(this.props.second, this.onClickSecond, 'clock-seconds');
    }
    return this.renderHours();
  }

  render() {
    return (
      <div className="react-clock">
        <svg role="img" aria-label='clock' width={this.props.size} height={this.props.size}>
          <g>
            <circle className='clock_face' cx={this.props.size / 2} cy={this.props.size / 2} r={(this.props.size / 2) * 0.93} />
            <circle className='origo' cx={this.props.size / 2} cy={this.props.size / 2} />
            {this.renderDisplayMode()}
          </g>
          <g>{this.renderClockHand()}</g>
        </svg>
      </div>
    );
  }
}
